// BeeHive.js — Community hub
// Visual identity: warm #100D07 amber-dark, NOT the app's cold #0A0A0A
// Tab: Community — hive groups. Journey & badges moved to Profile → Hive tab.
import { loadHiveGroups, getUsersForGroup } from '../utils/hiveGroups';
import { useTr } from '../utils/useTr';
import { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { HIVE_STORIES, getCurrentSeason } from '../utils/beeJourney';
import {
  MoodGrowingIcon, MoodExploringIcon, MoodReadyIcon,
  MoodConnectingIcon, MoodLowkeyIcon,
  HiveIcon,
} from '../components/icons';
import FoundingBadge from './../components/FoundingBadge';
import HivePulse from './../components/HivePulse';
import CoffeeRoulette from './../components/CoffeeRoulette';
import PeopleFilterPills from './../components/PeopleFilterPills';
import { RatingStreakBadge } from './../components/Gamification';

// ── Hex background ────────────────────────────────────────────────────────
function HexBg() {
  return (
    <div style={{ position:'absolute', inset:0, zIndex:0, pointerEvents:'none', overflow:'hidden' }}>
      <svg width="100%" height="100%">
        <defs>
          <pattern id="hive-bg" x="0" y="0" width="46" height="53" patternUnits="userSpaceOnUse">
            <polygon points="23,2 42,13 42,34 23,45 4,34 4,13"
              fill="none" stroke="rgba(201,168,76,0.08)" strokeWidth="0.8"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#hive-bg)"/>
      </svg>
      <div style={{ position:'absolute', inset:0, background:'radial-gradient(ellipse 90% 50% at 50% 0%, rgba(201,168,76,0.1) 0%, transparent 65%)' }}/>
    </div>
  );
}

// ── Member avatar bubble ──────────────────────────────────────────────────
function MemberBubble({ user, color, onClick, bh }) {
  const tr = useTr();
  const [hov, setHov] = useState(false);
  const mood = user.mood;
  const MOOD_ICONS = { growing:MoodGrowingIcon, exploring:MoodExploringIcon, ready:MoodReadyIcon, connecting:MoodConnectingIcon, lowkey:MoodLowkeyIcon };
  const MoodIcon = MOOD_ICONS[mood];

  return (
    <div
      onClick={() => onClick(user)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.35rem', cursor:'pointer', width:68, flexShrink:0 }}>
      <div style={{ position:'relative' }}>
        <div style={{
          width:54, height:54, overflow:'hidden',
          border:`2px solid ${hov ? color : color + '55'}`,
          transition:'border-color 0.2s, transform 0.2s',
          transform: hov ? 'scale(1.06)' : 'scale(1)',
          flexShrink:0,
        }}>
          <img src={user.photos[0]} alt={user.name}
            style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
        </div>
        {/* Mood dot */}
        {mood && (
          <div style={{ position:'absolute', bottom:-2, right:-2, fontSize:'0.7rem', lineHeight:1,
            background:bh.bg, border:`1px solid ${color}44`, padding:'1px', borderRadius:0 }}>
            {MoodIcon ? <MoodIcon size={10} color="rgba(201,168,76,0.9)"/> : null}
          </div>
        )}
        {/* Verified */}
        {user.verified && (
          <div style={{ position:'absolute', top:-2, right:-2, width:14, height:14,
            background:color, display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
        )}
      </div>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:'0.63rem', fontWeight:600, color:hov ? color : '#D4C4A0', transition:'color 0.2s', lineHeight:1.2, maxWidth:64, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis' }}>
          {user.name.split(' ')[0]}
        </div>
        <div style={{ fontSize:'0.55rem', color:bh.sub, lineHeight:1.2 }}>
          {user.city}
        </div>
      </div>
    </div>
  );
}

// ── Lifestyle section ─────────────────────────────────────────────────────
// ── Lifestyle Section — image strip with hover overlay + prev/next + popup
function LifestyleSection({ section, users, onOpenProfile, bh }) {
  const tr = useTr();
  const [offset, setOffset]     = useState(0);
  const [hovered, setHovered]   = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const perPage = 4; // images visible at once
  const maxOffset = Math.max(0, users.length - perPage);

  if (users.length === 0) return null;

  return (
    <div style={{ marginBottom:'0.15rem' }}>
      {/* Header — clickable to open popup */}
      <div
        onClick={() => setShowPopup(true)}
        style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'0.9rem 1rem 0.65rem', cursor:'pointer',
          background:`linear-gradient(90deg, ${section.color}0E, transparent)`,
          borderLeft:`3px solid ${section.color}`,
          borderBottom:`1px solid ${section.color}18`,
          transition:'background 0.2s',
        }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.65rem' }}>
          {section.Icon
            ? <section.Icon size={18} color={section.color}/>
            : <span style={{ fontSize:'1.1rem', lineHeight:1 }}>{section.emoji||'●'}</span>
          }
          <div>
            <div style={{ fontSize:'0.8rem', fontWeight:600, color:section.color, fontFamily:'Outfit', letterSpacing:'0.02em' }}>
              {section.label}
            </div>
            <div style={{ fontSize:'0.6rem', color:bh.sub, lineHeight:1.3 }}>{section.desc}</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexShrink:0 }}>
          <span style={{ fontSize:'0.6rem', color:section.color, background:section.color+'15', border:`1px solid ${section.color}33`, padding:'0.12rem 0.5rem', fontFamily:'Outfit', letterSpacing:'0.06em' }}>
            {users.length} {tr('common.members')}
          </span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={section.color} strokeWidth="1.5" strokeLinecap="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      </div>

      {/* Image strip — true CSS carousel, no gaps, buttons in overlay */}
      <div style={{ position:'relative', overflow:'hidden' }}>
        {/* Track: all images in a row, shifted by transform */}
        <div style={{
          display:'flex',
          transform:`translateX(calc(-${offset} * (100% / ${perPage})))`,
          transition:'transform 0.32s cubic-bezier(0.4,0,0.2,1)',
          willChange:'transform',
        }}>
          {users.map(user => (
            <div
              key={user.id}
              style={{
                position:'relative',
                width:`calc(100% / ${perPage})`,
                paddingTop:`calc(100% / ${perPage})`,
                flexShrink:0,
                cursor:'pointer',
                overflow:'hidden',
              }}
              onMouseEnter={() => setHovered(user.id)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onOpenProfile(user)}
            >
              {/* Fallback background — behind the img */}
              <div style={{ position:'absolute', inset:0, background:'var(--dark)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.4rem', color:'rgba(201,168,76,0.4)' }}>{(user.name||'')[0]}</span>
              </div>
              <img
                src={(user.photos||'')[0] || user.photo}
                alt={user.name}
                style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', display:'block', zIndex:1 }}
              />
              {/* Name/age hover overlay */}
              <div style={{
                position:'absolute', inset:0, zIndex:2,
                background:'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 50%)',
                opacity: hovered === user.id ? 1 : 0,
                transition:'opacity 0.18s',
                display:'flex', flexDirection:'column', justifyContent:'flex-end',
                padding:'0.4rem 0.45rem',
                pointerEvents:'none',
              }}>
                <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#FFF', fontFamily:'Outfit', lineHeight:1.1 }}>
                  {user.name?.split(' ')[0]}
                </div>
                <div style={{ fontSize:'0.58rem', color:'rgba(255,255,255,0.85)' }}>
                  {user.age}
                </div>
              </div>
              {/* Right border */}
              <div style={{ position:'absolute', top:0, right:0, bottom:0, width:1, background:'rgba(0,0,0,0.18)', zIndex:3 }}/>
            </div>
          ))}
        </div>

        {/* Prev button — overlay, only when not at start */}
        {offset > 0 && (
          <button
            onClick={e => { e.stopPropagation(); setOffset(o => Math.max(0, o - perPage)); }}
            style={{
              position:'absolute', left:0, top:0, bottom:0, width:36, zIndex:10,
              background:'linear-gradient(to right, rgba(0,0,0,0.55) 0%, transparent 100%)',
              border:'none', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
        )}
        {/* Next button */}
        {offset < maxOffset && (
          <button
            onClick={e => { e.stopPropagation(); setOffset(o => Math.min(maxOffset, o + perPage)); }}
            style={{
              position:'absolute', right:0, top:0, bottom:0, width:36, zIndex:10,
              background:'linear-gradient(to left, rgba(0,0,0,0.55) 0%, transparent 100%)',
              border:'none', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center',
            }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        )}
      </div>

      {/* Tags row */}
      <div style={{ padding:'0.5rem 1rem 0.8rem', display:'flex', flexWrap:'wrap', gap:'0.3rem' }}>
        {(section.tags||[]).slice(0, 8).map(tag => {
          const count = users.filter(u => (u.interests||[]).includes(tag)).length;
          if (count === 0) return null;
          return (
            <span key={tag} style={{ fontSize:'0.56rem', color:section.color, background:section.color+'0E',
              border:`1px solid ${section.color}22`, padding:'0.1rem 0.45rem', fontFamily:'Outfit' }}>
              {tag} · {count}
            </span>
          );
        })}
      </div>

      {/* Section popup */}
      {showPopup && (
        <SectionPopup section={section} users={users} bh={bh}
          onClose={() => setShowPopup(false)} onOpenProfile={onOpenProfile}/>
      )}
    </div>
  );
}

// ── Section popup — full member list ─────────────────────────────────────
function SectionPopup({ section, users, bh, onClose, onOpenProfile }) {
  const tr = useTr();
  const [hovered, setHovered] = useState(null);
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:700, background:'rgba(0,0,0,0.78)', backdropFilter:'blur(8px)', display:'flex', flexDirection:'column', justifyContent:'flex-end', alignItems:'center' }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:bh.bg, border:`1px solid ${section.color}44`, borderBottom:'none', maxWidth:480, width:'100%', maxHeight:'88vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {/* Header */}
        <div style={{ padding:'1rem 1.1rem 0.8rem', borderBottom:`1px solid ${section.color}22`, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.6rem' }}>
            {section.Icon
            ? <section.Icon size={18} color={section.color}/>
            : <span style={{ fontSize:'1.1rem', lineHeight:1 }}>{section.emoji||'●'}</span>
          }
            <div>
              <div style={{ fontFamily:'Outfit', fontSize:'0.85rem', fontWeight:700, color:section.color }}>{section.label}</div>
              <div style={{ fontSize:'0.6rem', color:bh.sub }}>{users.length} {tr('common.members')}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', padding:'0.3rem' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={bh.muted} strokeWidth="1.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        {/* Grid of images — same flush style */}
        <div style={{ flex:1, overflowY:'auto' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:0 }}>
            {users.map(user => (
              <div key={user.id}
                style={{ position:'relative', paddingTop:'100%', cursor:'pointer', overflow:'hidden' }}
                onMouseEnter={() => setHovered(user.id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => { onOpenProfile(user); onClose(); }}
              >
                <div style={{ position:'absolute', inset:0, background:'var(--dark)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', color:'rgba(201,168,76,0.4)' }}>{(user.name||'')[0]}</span>
                </div>
                <img src={(user.photos||'')[0] || user.photo} alt={user.name}
                  style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover', zIndex:1 }}
                />
                <div style={{
                  position:'absolute', inset:0,
                  background:'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 50%)',
                  opacity: hovered === user.id ? 1 : 0,
                  transition:'opacity 0.18s',
                  display:'flex', flexDirection:'column', justifyContent:'flex-end',
                  padding:'0.5rem 0.55rem',
                }}>
                  <div style={{ fontSize:'0.75rem', fontWeight:700, color:'#FFF', fontFamily:'Outfit' }}>
                    {user.name.split(' ')[0]}, {user.age}
                  </div>
                  <div style={{ fontSize:'0.58rem', color:'rgba(255,255,255,0.75)' }}>{user.city}</div>
                </div>
                <div style={{ position:'absolute', right:0, bottom:0, width:1, background:bh.border }}/>
                <div style={{ position:'absolute', left:0, right:0, bottom:0, height:1, background:bh.border }}/>
              </div>
            ))}
          </div>
        </div>
        {/* Tags */}
        <div style={{ padding:'0.75rem 1rem', borderTop:`1px solid ${section.color}22`, flexShrink:0, display:'flex', flexWrap:'wrap', gap:'0.35rem' }}>
          {(section.tags||[]).slice(0,10).map(tag => {
            const count = users.filter(u=>(u.interests||[]).includes(tag)).length;
            if (!count) return null;
            return <span key={tag} style={{ fontSize:'0.58rem', color:section.color, background:section.color+'10', border:`1px solid ${section.color}28`, padding:'0.12rem 0.5rem', fontFamily:'Outfit' }}>{tag} · {count}</span>;
          })}
        </div>
      </div>
    </div>
  );
}

// ── Profile mini sheet ────────────────────────────────────────────────────
function ProfileSheet({ user, onClose, onOpen, bh }) {
  const tr = useTr();
  if (!user) return null;
  return (
    <div style={{ position:'fixed', inset:0, zIndex:600, display:'flex', flexDirection:'column', justifyContent:'flex-end' }}
      onClick={onClose}>
      <div style={{ background:bh.bg, border:`1px solid ${bh.border}`, borderBottom:'none', padding:'1.2rem', maxWidth:480, width:'100%', margin:'0 auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', gap:'1rem', alignItems:'center', marginBottom:'1rem' }}>
          <img src={user.photos[0]} alt="" style={{ width:60, height:60, objectFit:'cover', border:'2px solid rgba(201,168,76,0.4)' }}/>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.2rem', color:bh.text }}>{user.name}, {user.age}</div>
              <FoundingBadge user={user} size="sm"/>
            </div>
            <div style={{ fontSize:'0.68rem', color:bh.sub }}>{user.job} · {user.city}</div>
            {user.interests && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:'0.25rem', marginTop:'0.4rem' }}>
                {user.interests.slice(0,4).map(i => (
                  <span key={i} style={{ fontSize:'0.55rem', color:'rgba(201,168,76,0.7)', background:bh.card, border:'1px solid rgba(201,168,76,0.18)', padding:'0.08rem 0.4rem' }}>{i}</span>
                ))}
              </div>
            )}
          </div>
        </div>
        {user.bio && <p style={{ fontSize:'0.75rem', color:bh.muted, lineHeight:1.6, margin:'0 0 1rem' }}>{user.bio}</p>}
        <div style={{ display:'flex', gap:'0.6rem' }}>
          <button onClick={onClose} style={{ flex:1, background:bh.card, border:`1px solid ${bh.border}`, color:'rgba(201,168,76,0.8)', padding:'0.6rem', cursor:'pointer', fontSize:'0.7rem', fontFamily:'Outfit', letterSpacing:'0.06em' }}>
            {tr('hive.close')}
          </button>
          <button onClick={() => onOpen(user)} style={{ flex:2, background:'rgba(201,168,76,0.9)', border:'none', color:'#0A0A0A', padding:'0.6rem', cursor:'pointer', fontSize:'0.72rem', fontWeight:700, fontFamily:'Outfit', letterSpacing:'0.07em' }}>
            {tr('hive.open_profile')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Question du jour ──────────────────────────────────────────────────────
const WEEKLY_QUESTIONS = [
  { q: 'Quel endroit à Paris te ressemble vraiment ?', tag: 'lieu' },
  { q: 'Si tu devais décrire ton idéal de week-end, ce serait quoi ?', tag: 'lifestyle' },
  { q: 'La dernière chose qui t\'a vraiment surpris(e) ?', tag: 'curiosité' },
  { q: 'Qu\'est-ce que tu fais quand tu veux vraiment décompresser ?', tag: 'bien-être' },
  { q: 'Une conviction que tu as changé ces dernières années ?', tag: 'valeurs' },
  { q: 'Ce que les gens ne voient pas en toi au premier abord ?', tag: 'identité' },
  { q: 'La chose que tu fais toujours en solo et que tu adores ?', tag: 'solo' },
  { q: 'Si tu pouvais apprendre n\'importe quoi en une semaine, ce serait quoi ?', tag: 'curiosité' },
  { q: 'Un rituel que tu ne sacrifierais pour rien au monde ?', tag: 'lifestyle' },
  { q: 'Ce qui t\'attire d\'abord chez quelqu\'un que tu rencontres ?', tag: 'connexion' },
  { q: 'La dernière fois que tu as fait quelque chose pour la première fois ?', tag: 'aventure' },
  { q: 'Qu\'est-ce qui te donne de l\'énergie vs ce qui t\'en prend ?', tag: 'énergie' },
];

function QuestionDuJour({ currentUser, bh }) {
  const tr = useTr();
  const weekNum = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
  const question = WEEKLY_QUESTIONS[weekNum % WEEKLY_QUESTIONS.length];
  const storageKey = `beeloveQDJ_${weekNum}`;
  const [answer, setAnswer] = useState(() => { try { return localStorage.getItem(storageKey) || ''; } catch { return ''; } });
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(() => { try { return !!localStorage.getItem(storageKey); } catch { return false; } });

  const saveAnswer = () => {
    if (!answer.trim()) return;
    try { localStorage.setItem(storageKey, answer.trim()); } catch {}
    setSaved(true);
    setEditing(false);
  };

  return (
    <div style={{ margin:'0.9rem 1rem 0', padding:'0.85rem 1rem', background:'rgba(201,168,76,0.06)', border:'1px solid rgba(201,168,76,0.22)', borderRadius:'var(--r-md)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.6rem' }}>
        <span style={{ fontSize:'0.85rem' }}>✦</span>
        <span style={{ fontSize:'0.58rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'#C9A84C' }}>{tr('hive.weekly_question')}</span>
        <span style={{ marginLeft:'auto', fontSize:'0.55rem', padding:'1px 6px', borderRadius:99, background:'rgba(201,168,76,0.15)', border:'1px solid rgba(201,168,76,0.3)', color:'#C9A84C' }}>{question.tag}</span>
      </div>
      <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1rem', color:bh.text, lineHeight:1.5, margin:'0 0 0.75rem', fontStyle:'italic' }}>
        « {question.q} »
      </p>
      {!saved && !editing && (
        <button
          onClick={() => setEditing(true)}
          style={{ fontSize:'0.72rem', padding:'0.4rem 0.9rem', background:'rgba(201,168,76,0.12)', border:'1px solid rgba(201,168,76,0.35)', borderRadius:99, color:'#C9A84C', cursor:'pointer', fontFamily:'Outfit' }}
        >
          {tr('hive.answer_btn')}
        </button>
      )}
      {editing && (
        <div>
          <textarea
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            placeholder={tr('hive.answer_ph')}
            rows={3}
            style={{ width:'100%', padding:'0.6rem 0.75rem', background:'var(--dark)', border:'1px solid rgba(201,168,76,0.3)', borderRadius:'var(--r-sm)', color:bh.text, fontFamily:'Outfit', fontSize:'0.8rem', resize:'none', outline:'none', boxSizing:'border-box', marginBottom:'0.5rem' }}
          />
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button onClick={() => setEditing(false)} style={{ padding:'0.35rem 0.8rem', background:'transparent', border:'1px solid var(--border)', borderRadius:99, color:bh.muted, fontSize:'0.68rem', cursor:'pointer', fontFamily:'Outfit' }}>{tr('common.cancel')}</button>
            <button onClick={saveAnswer} style={{ padding:'0.35rem 0.9rem', background:'rgba(201,168,76,0.15)', border:'1px solid rgba(201,168,76,0.4)', borderRadius:99, color:'#C9A84C', fontSize:'0.68rem', fontWeight:600, cursor:'pointer', fontFamily:'Outfit' }}>{tr('common.save')}</button>
          </div>
        </div>
      )}
      {saved && !editing && (
        <div>
          <p style={{ fontSize:'0.78rem', color:bh.text, lineHeight:1.5, margin:'0 0 0.4rem', borderLeft:'2px solid rgba(201,168,76,0.4)', paddingLeft:'0.6rem', fontStyle:'italic' }}>{answer}</p>
          <button onClick={() => setEditing(true)} style={{ fontSize:'0.62rem', color:bh.muted, background:'none', border:'none', cursor:'pointer', fontFamily:'Outfit', padding:0 }}>{tr('common.edit')}</button>
        </div>
      )}
    </div>
  );
}

// ── Weekly Challenges ─────────────────────────────────────────────────────
const GROUP_CHALLENGES = {
  active:       ['Partagez une photo de votre sortie de la semaine', 'Proposez un spot outdoor à découvrir', 'Planifiez une session groupe ce week-end'],
  food:         ['Recommandez un restaurant avec un avis sincère', 'Partagez une recette que vous aimez', 'Organisez un apéro improvisé'],
  creative:     ['Partagez une découverte musicale ou artistique', 'Recommandez un film à voir absolument', 'Proposez une sortie culturelle collective'],
  mindful:      ['Partagez votre pratique du moment', 'Recommandez un livre ou podcast bien-être', 'Proposez une marche méditative en groupe'],
  intellectual: ['Partagez un article qui vous a marqué', 'Proposez une question ouverte au groupe', 'Organisez une session lecture ou débat'],
  night_owls:   ['Partagez votre adresse nocturne préférée', 'Proposez une sortie tard ce soir', 'Recommandez un lieu secret nocturne'],
  plant_based:  ['Partagez votre recette végane favorite', 'Recommandez un resto plant-based', 'Partagez votre astuce alimentation'],
  exploring:    ['Dites où vous en êtes dans votre exploration', 'Partagez ce que vous cherchez vraiment', 'Proposez un échange sincère'],
  french_speakers: ['Partagez un mot français que vous aimez', 'Proposez une sortie en français', 'Recommandez un lieu francophone'],
  highly_compatible: ['Prenez contact avec quelqu\'un de compatible', 'Partagez ce qui vous a surpris dans vos matchs', 'Envoyez un message d\'introduction'],
};

function WeeklyChallenges({ groupId, color, bh }) {
  const tr = useTr();
  const weekNum = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
  const allChallenges = GROUP_CHALLENGES[groupId] || GROUP_CHALLENGES.exploring;
  // Pick 2 challenges based on week number
  const challenges = [
    allChallenges[weekNum % allChallenges.length],
    allChallenges[(weekNum + 1) % allChallenges.length],
  ];
  const [done, setDone] = useState({});

  return (
    <div style={{ padding:'0.9rem', background:`${color}0A`, border:`1px solid ${color}25`, borderRadius:'var(--r-md)', marginBottom:'0.8rem' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.65rem' }}>
        <span style={{ fontSize:'0.7rem' }}>✦</span>
        <p style={{ fontSize:'0.6rem', letterSpacing:'0.14em', textTransform:'uppercase', color, margin:0 }}>{tr('hive.weekly_challenge')}</p>
      </div>
      {challenges.map((ch, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.6rem', marginBottom:'0.4rem', opacity: done[i] ? 0.5 : 1, transition:'opacity 0.2s' }}>
          <button
            onClick={() => setDone(d => ({ ...d, [i]: !d[i] }))}
            style={{ width:18, height:18, borderRadius:4, border:`1.5px solid ${done[i] ? color : bh.border}`, background: done[i] ? color : 'transparent', flexShrink:0, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#0A0A0A', fontSize:'0.6rem' }}
          >
            {done[i] ? '✓' : ''}
          </button>
          <span style={{ fontSize:'0.75rem', color:bh.text, lineHeight:1.4, textDecoration: done[i] ? 'line-through' : 'none' }}>{ch}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────
export default function BeeHive() {
  const { features, lang } = useApp();
  const tr = useTr();

  if (features?.beehive === false) {
    return (
      <div style={{ minHeight:'100vh', background:'var(--black)', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem', textAlign:'center' }}>
        <div>
          <div style={{ marginBottom:'1rem', display:'flex', justifyContent:'center' }}><HiveIcon size={48} color="var(--gold)"/></div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.6rem', fontWeight:400, color:'var(--white)', marginBottom:'0.5rem' }}>
            {tr('hive.coming_soon')}
          </h2>
          <p style={{ fontSize:'0.82rem', color:'var(--muted)', lineHeight:1.7 }}>
            {tr('common.stay_tuned')}
          </p>
        </div>
      </div>
    );
  }

  const { currentUser, candidates, setPage, setDetailUser, theme, getCompatibility } = useApp();
  // Local people filter — independent of Discover/BeeConnect so a filter on
  // Discover never empties this page. Pills look identical (same vocabulary)
  // but state is owned by BeeHive.
  const [hiveFilter, setHiveFilter] = useState('all');
  const networkUsers = (candidates || [])
    .filter(u => u.id !== currentUser?.id)
    .filter(u => {
      if (!hiveFilter || hiveFilter === 'all') return true;
      if (hiveFilter === 'verified')   return !!u.verified;
      if (hiveFilter === 'nearby')     return u.distance != null ? u.distance <= 15 : (u.distance_km != null ? u.distance_km <= 15 : true);
      if (hiveFilter === 'compatible') {
        const c = getCompatibility?.(u.id);
        return c && c.score >= 70;
      }
      if (hiveFilter === 'friends')    return u.mood === 'connecting' || u.seekingFriends || u.app_mode === 'friendly';
      return true;
    });
  const isDark = theme !== 'light';
  const bh = isDark
    ? { bg:'#100D07', card:'rgba(201,168,76,0.07)', border:'rgba(201,168,76,0.18)', text:'#F2E8D0', muted:'#C4B080', sub:'#8A7A5A' }
    : { bg:'#FDF8EF', card:'rgba(138,94,8,0.07)',   border:'rgba(138,94,8,0.20)',   text:'#1A1005', muted:'#4A3510',  sub:'#6A5525' };
  const [tab, setTab] = useState(() => {
    const saved = sessionStorage.getItem('hiveTab');
    if (saved) { sessionStorage.removeItem('hiveTab'); return saved; }
    return 'community';
  });
  const [sheetUser, setSheetUser] = useState(null);
  // users from context

  // Load configurable hive groups
  const [activeGroupId, setActiveGroupId] = useState('all');
  const hiveGroupDefs = loadHiveGroups(lang).filter(g => g.enabled && g.visibleInCommunity)
    .sort((a,b) => (a.sortOrder||0) - (b.sortOrder||0));

  const now = Date.now();
  const sections = hiveGroupDefs.map(group => ({
    ...group,
    Icon: null,
    users: getUsersForGroup(group, networkUsers, currentUser, getCompatibility)
      .sort((a, b) => {
        // Boosted users sort first in their target group
        const aBoosted = a.hive_boost_group === group.id && a.hive_boost_until && new Date(a.hive_boost_until) > now;
        const bBoosted = b.hive_boost_group === group.id && b.hive_boost_until && new Date(b.hive_boost_until) > now;
        return bBoosted - aBoosted;
      }),
  })).filter(s => s.users.length > 0);

  // Filtered sections for active group selector
  const displaySections = activeGroupId === 'all'
    ? sections
    : sections.filter(s => s.id === activeGroupId);

  const season = getCurrentSeason();

  const TABS = [
    { id:'community', label:tr('hive.tab_community') },
  ];

  const openProfile = (user) => {
    setSheetUser(null);
    if (setDetailUser) setDetailUser(user);
    else setPage('discover');
  };

  const totalMembers = [...new Set(sections.flatMap(s => s.users.map(u => u.id)))].length;

  return (
    <div style={{ minHeight:'calc(100vh - var(--nav-h) - var(--bottom-h))', position:'relative', background:bh.bg, color:bh.text }}>
      <HexBg/>

      <div style={{ position:'relative', zIndex:1 }}>

        {/* ── Amber hero ── */}
        <div style={{ padding:'1.2rem 1rem 0', borderBottom:'1px solid rgba(201,168,76,0.16)', background:'linear-gradient(180deg, rgba(201,168,76,0.09) 0%, transparent 100%)' }}>

          {/* Season badge */}
          <div style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.25rem 0.7rem', background:season.color+'16', border:`1px solid ${season.color}44`, marginBottom:'1rem' }}>
            <HiveIcon size={16} color={season.color || '#C9A84C'}/>
            <span style={{ fontSize:'0.58rem', letterSpacing:'0.13em', textTransform:'uppercase', color:season.color }}>{tr(season.labelKey)}</span>
          </div>

          {/* Headline + member count */}
          <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', marginBottom:'0.9rem' }}>
            <div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.55rem', color:bh.text, lineHeight:1.1, marginBottom:'0.3rem' }}>
                {tr('beehive.title')}
              </div>
              <div style={{ fontSize:'0.65rem', color:bh.sub, lineHeight:1.5 }}>
                {tr('hive.earned_place')}<br/>{tr('hive.find_people')}
              </div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0, paddingLeft:'1rem' }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.8rem', color:'#C9A84C', fontWeight:700, lineHeight:1 }}>{totalMembers}</div>
              <div style={{ fontSize:'0.56rem', color:bh.sub, textTransform:'uppercase', letterSpacing:'0.1em' }}>{tr('common.members')}</div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex' }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                flex:1, background:'none', border:'none', cursor:'pointer',
                borderBottom:`2px solid ${tab===t.id ? '#C9A84C' : 'transparent'}`,
                padding:'0.65rem 0', fontSize:'0.63rem', fontWeight:tab===t.id ? 700 : 400,
                letterSpacing:'0.1em', textTransform:'uppercase',
                color:tab===t.id ? 'var(--gold)' : bh.muted,
                transition:'all 0.2s', fontFamily:'Outfit',
              }}>{t.label}</button>
            ))}
          </div>
        </div>

        {/* Same pills as Discover & BeeConnect — but local state */}
        <PeopleFilterPills theme="amber" value={hiveFilter} onChange={setHiveFilter}/>

        {/* Feature L — Hive Pulse (real-time online + mood) */}
        <HivePulse bh={bh}/>

        {/* Feature E — Rating streak badge (own profile signal) */}
        <div style={{ padding: '0.6rem 1rem 0' }}>
          <RatingStreakBadge inline lang={lang}/>
        </div>

        {/* ── COMMUNITY tab ── */}
        {tab === 'community' && (
          <div>
            {/* ── Season card ── */}
            <div style={{ display:'flex', alignItems:'center', gap:'0.8rem', padding:'0.85rem 1rem', margin:'0.9rem 1rem 0', background:season.color+'0E', border:`1px solid ${season.color}33` }}>
              <HiveIcon size={22} color={season.color||'#C9A84C'}/>
              <div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1rem', color:season.color }}>{tr(season.labelKey)}</div>
                <div style={{ fontSize:'0.62rem', color:bh.sub }}>{tr('hive.season_theme')}</div>
              </div>
            </div>

            {/* Question du jour (cette semaine dans la ruche) */}
            <QuestionDuJour currentUser={currentUser} bh={bh}/>

            {/* Journey link — points to Profile Hive tab */}
            <div onClick={() => { import('../utils/navigate').then(({ navigate }) => navigate('profile')); }}
              style={{ display:'flex', alignItems:'center', gap:'0.85rem', padding:'0.85rem 1rem', margin:'0.9rem 1rem 0',
                background:bh.card, border:`1px solid ${bh.border}`, cursor:'pointer' }}>
              <HiveIcon size={22} color='#C9A84C'/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:'0.76rem', fontWeight:600, color:bh.text, fontFamily:'Outfit', marginBottom:'0.1rem' }}>
                  {tr('hive.my_journey')||'My Journey & Badges'}
                </div>
                <div style={{ fontSize:'0.62rem', color:bh.sub }}>
                  {tr('hive.journey_in_profile')||'View your progress and badges in your profile →'}
                </div>
              </div>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={bh.sub} strokeWidth="1.5" strokeLinecap="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </div>
            {/* Intro blurb */}
            <div style={{ padding:'1rem 1rem 0' }}>
              <p style={{ fontSize:'0.72rem', color:bh.sub, lineHeight:1.7, margin:0 }}>
                {tr('hive.subtitle')}
              </p>
            </div>

            {/* ── Group selector bar ── */}
            <div style={{ display:'flex', gap:'0.4rem', overflowX:'auto', padding:'0.9rem 1rem 0', scrollbarWidth:'none', msOverflowStyle:'none' }}>
              {/* All button */}
              <button onClick={() => setActiveGroupId('all')}
                style={{ flexShrink:0, display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.3rem 0.75rem', border:`1px solid ${activeGroupId==='all' ? 'rgba(201,168,76,0.6)' : bh.border}`, borderRadius:'var(--r-pill)', background: activeGroupId==='all' ? 'rgba(201,168,76,0.12)' : 'transparent', color: activeGroupId==='all' ? '#C9A84C' : bh.muted, fontFamily:'Outfit', fontSize:'0.65rem', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                {tr('beehive.all_groups')}
                <span style={{ fontSize:'0.55rem', background:'var(--border2)', padding:'0.05rem 0.3rem', borderRadius:99, marginLeft:'0.2rem' }}>{sections.reduce((n,s)=>n+s.users.length,0)}</span>
              </button>
              {sections.map(s => (
                <button key={s.id} onClick={() => setActiveGroupId(s.id)}
                  style={{ flexShrink:0, display:'flex', alignItems:'center', gap:'0.3rem', padding:'0.3rem 0.75rem', border:`1px solid ${activeGroupId===s.id ? (s.color+'99') : bh.border}`, borderRadius:'var(--r-pill)', background: activeGroupId===s.id ? (s.color+'18') : 'transparent', color: activeGroupId===s.id ? s.color : bh.muted, fontFamily:'Outfit', fontSize:'0.65rem', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap', transition:'all 0.15s' }}>
                  <span>{s.emoji||'●'}</span>
                  {s.label}
                  <span style={{ fontSize:'0.55rem', background:'var(--border2)', padding:'0.05rem 0.3rem', borderRadius:99 }}>{s.users.length}</span>
                </button>
              ))}
            </div>

            {/* Community sections */}
            <div style={{ marginTop:'0.8rem' }}>
              {displaySections.map(section => (
                <div key={section.id}>
                  <LifestyleSection
                    section={section}
                    users={section.users}
                    onOpenProfile={setSheetUser} bh={bh}
                  />
                  {/* Feature N — Coffee Roulette per group (gated by feature flag inside) */}
                  <CoffeeRoulette groupId={section.id} groupName={section.label} bh={bh}/>
                  <div style={{ padding:'0 1rem', marginBottom:'0.5rem' }}>
                    <WeeklyChallenges groupId={section.id} color={section.color || '#C9A84C'} bh={bh}/>
                  </div>
                </div>
              ))}
              {displaySections.length === 0 && (
                <div style={{ padding:'2rem 1rem', textAlign:'center', color:bh.muted, fontSize:'0.78rem' }}>
                  No members in this group yet.
                </div>
              )}
            </div>

            {/* Hive Wall strip */}
            <div style={{ padding:'1.2rem 1rem 0' }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.1rem', color:'#C9A84C', marginBottom:'0.5rem' }}>{tr('beehive.stories_title')}</div>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem', marginBottom:'1rem' }}>
                {HIVE_STORIES.slice(0,3).map(story => {
                  const pal = { match:'#C9A84C', friendship:'#A084C4', community:'#9CA880' };
                  const col = pal[story.type] || '#888880';
                  return (
                    <div key={story.id} style={{ padding:'0.9rem', background:col+'0A', border:`1px solid ${col}1E`, position:'relative', overflow:'hidden' }}>
                      <div style={{ position:'absolute', top:-8, right:-6, fontSize:'3.5rem', opacity:0.04, pointerEvents:'none' }}>⬡</div>
                      <div style={{ display:'flex', gap:'0.6rem', alignItems:'flex-start' }}>
                        <HiveIcon size={18} color={col}/>
                        <div>
                          <p style={{ fontSize:'0.78rem', color:bh.muted, lineHeight:1.6, margin:'0 0 0.3rem', fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic' }}>"{story.text}"</p>
                          <span style={{ fontSize:'0.55rem', color:col, textTransform:'uppercase', letterSpacing:'0.1em' }}>{story.type} · {story.city}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}


      </div>

      {/* Profile sheet */}
      <ProfileSheet user={sheetUser} onClose={() => setSheetUser(null)} onOpen={openProfile} bh={bh}/>
    </div>
  );
}
